/**
 * Werkbon-helpers voor het v2-model (zie BACKEND_WORK_ORDER_MODEL_V2_SPEC.md).
 *
 * Drie losstaande feiten, elk met een eigen veld:
 * - Afspraak:        `appointment.start` (lezen) / `appointment_start` (schrijven)
 * - Klantbevestiging: `appointment_confirmed_at` (NULL = voorlopig)
 * - Materialen:      `materials_ready_at` (NULL = nog niet binnen)
 *
 * De levenscyclus is `open` → `in_progress` → `completed` / `cancelled`.
 * Zolang backend fase 3 (opruimen) nog niet gedraaid is, geeft het
 * `status`-veld nog de oude gedetailleerde waarden terug; de lifecycle-map
 * hieronder vangt beide vocabulaires af.
 */

const LIFECYCLE_MAP = {
  // Oude waarden (tot backend fase 3)
  pending: 'open',
  scheduled: 'open',
  ready_for_installation: 'open',
  // v2-waarden
  open: 'open',
  in_progress: 'in_progress',
  completed: 'completed',
  cancelled: 'cancelled',
};

/**
 * Startmoment van de afspraak (ISO string) of null als niet ingepland.
 * v1-fallback (oude datumvelden) tot de v2-velden aantoonbaar op productie
 * geleverd worden — NIET verwijderen vóór die check.
 */
export function getAppointmentStart(workOrder) {
  return (
    workOrder?.appointment?.start ||
    workOrder?.appointment_start ||
    workOrder?.confirmed_appointment_date ||
    workOrder?.tentative_appointment_date ||
    null
  );
}

/**
 * Moment waarop de afspraak met de klant bevestigd is, of null (= voorlopig).
 */
export function getAppointmentConfirmedAt(workOrder) {
  return (
    workOrder?.appointment?.confirmed_at ||
    workOrder?.appointment_confirmed_at ||
    workOrder?.confirmed_appointment_date ||
    null
  );
}

export function isPlanned(workOrder) {
  return !!getAppointmentStart(workOrder);
}

export function isAppointmentConfirmed(workOrder) {
  return !!getAppointmentConfirmedAt(workOrder);
}

/** Ingepland maar nog niet bevestigd met de klant. */
export function isTentativelyPlanned(workOrder) {
  return isPlanned(workOrder) && !isAppointmentConfirmed(workOrder);
}

/**
 * Levenscyclus-status: 'open' | 'in_progress' | 'completed' | 'cancelled'.
 */
export function getLifecycleStatus(workOrder) {
  return LIFECYCLE_MAP[workOrder?.status] || 'open';
}

/** Moment waarop de materialen gereed gemeld zijn, of null. */
export function getMaterialsReadyAt(workOrder) {
  return workOrder?.materials_ready_at || null;
}

/**
 * Materialen-indicator voor kaarten/lijsten: 'in' | 'waiting' | 'none'.
 *
 * 'none' betekent: niet relevant (bon is bezig/afgerond/geannuleerd, of er
 * hangt geen order aan de bon en er valt dus niets te ontvangen).
 */
const ORDER_RECEIVED_STATUSES = ['received', 'ready_for_installation', 'installed', 'completed'];

export function getMaterialsState(workOrder) {
  if (getLifecycleStatus(workOrder) !== 'open') return 'none';
  // Een handmatig gezette vlag wint altijd.
  if (getMaterialsReadyAt(workOrder) && workOrder?.materials_ready_source === 'manual') return 'in';
  // Backend-afgeleide status over álle orders van het project (multi-leverancier).
  // Gaat vóór een (mogelijk verouderde) order-gezette vlag.
  const derived = workOrder?.materials?.status;
  if (derived === 'ready') return 'in';
  if (derived === 'partial' || derived === 'waiting') return 'waiting';
  if (derived === 'none') return getMaterialsReadyAt(workOrder) ? 'in' : 'none';
  if (getMaterialsReadyAt(workOrder)) return 'in';
  // v1-fallbacks: werkbonstatus en orderstatus
  if (workOrder?.status === 'ready_for_installation') return 'in';
  const orderStatus = workOrder?.order?.status;
  if (orderStatus && ORDER_RECEIVED_STATUSES.includes(orderStatus)) return 'in';
  return workOrder?.order || workOrder?.order_id ? 'waiting' : 'none';
}

/**
 * Korte materialen-samenvatting voor lijsten/kaarten, of null als er niets
 * te melden is. Bijvoorbeeld "1/2 binnen · verwacht 14 okt" of
 * "Wacht op Allround · verwacht 14 okt".
 */
export function getMaterialsSummary(workOrder) {
  const state = getMaterialsState(workOrder);
  if (state === 'none') return null;

  const materials = workOrder?.materials;
  if (state === 'in') {
    return { state, label: 'Materialen binnen', detail: null };
  }

  const total = materials?.total_count || 0;
  const received = materials?.received_count || 0;
  const pending = (materials?.orders || []).filter((o) => !o.received);
  const suppliers = [...new Set(pending.map((o) => o.supplier_name).filter(Boolean))];

  let label;
  if (total > 1) {
    label = `${received}/${total} binnen`;
  } else if (suppliers.length === 1) {
    label = `Wacht op ${suppliers[0]}`;
  } else {
    label = 'Wacht op levering';
  }

  let detail = null;
  if (materials?.expected_complete_date) {
    const d = new Date(`${materials.expected_complete_date}T12:00:00`);
    if (!Number.isNaN(d.getTime())) {
      detail = `verwacht ${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`;
    }
  }
  if (total > 1 && suppliers.length) {
    const who = suppliers.join(', ');
    detail = detail ? `${who} · ${detail}` : who;
  }

  return { state, label, detail };
}

/**
 * Afgeleide weergave-state, voor kleurpaletten, filters en labels:
 * 'pending' | 'scheduled' | 'ready_for_installation' | 'in_progress' | 'completed' | 'cancelled'
 *
 * Gebruikt `derived_state` van de backend als die er is; berekent hem anders
 * lokaal (nodig direct na optimistische updates).
 */
export function getDerivedState(workOrder) {
  if (workOrder?.derived_state) return workOrder.derived_state;
  const lifecycle = getLifecycleStatus(workOrder);
  if (lifecycle !== 'open') return lifecycle;
  if (getMaterialsState(workOrder) === 'in') return 'ready_for_installation';
  return isPlanned(workOrder) ? 'scheduled' : 'pending';
}

/**
 * Bouw de afspraak-velden voor een PATCH /work-orders/:id payload.
 *
 * Volgt de v2-invarianten: verplaatsen wist een bestaande bevestiging niet
 * (het oorspronkelijke bevestigingsmoment blijft staan), bevestigen zet het
 * moment op nu, en de status wordt niet aangeraakt.
 *
 * @param {Object} workOrder - Huidige werkbon (voor bestaande bevestiging)
 * @param {Object} opts
 * @param {string} opts.startIso - Afspraakstart (ISO string)
 * @param {number} opts.durationMinutes
 * @param {boolean} opts.confirmed - Is/blijft de afspraak bevestigd met de klant?
 * @returns {Object} Te mergen in de update-payload
 */
export function buildAppointmentPayload(workOrder, { startIso, durationMinutes, confirmed }) {
  const confirmedAt = confirmed
    ? getAppointmentConfirmedAt(workOrder) || new Date().toISOString()
    : null;

  // Alleen v2-velden — legacy tentative/confirmed_appointment_date geven 422
  // (backend fase 3: legacy_field_removed).
  return {
    appointment_start: startIso,
    appointment_duration_minutes: durationMinutes,
    appointment_confirmed_at: confirmedAt,
  };
}

/**
 * Status na het (her)inplannen. Houdt bestaande v2/open-statussen;
 * legacy `scheduled`/`pending` worden gemapt naar `open` (fase 3 weigert
 * oude enum-waarden als input).
 */
export function statusAfterScheduling(workOrder) {
  const keep = ['ready_for_installation', 'in_progress', 'open'];
  return keep.includes(workOrder?.status) ? workOrder.status : 'open';
}

/**
 * Pas een appointment-payload (uit buildAppointmentPayload) toe op een lokaal
 * werkbon-object, inclusief het geneste `appointment`-object uit responses.
 */
export function applyAppointmentLocal(workOrder, payload) {
  // Server-berekende derived_state is na een lokale wijziging verouderd;
  // wissen zodat getDerivedState hem lokaal opnieuw berekent.
  workOrder.derived_state = undefined;
  workOrder.appointment_start = payload.appointment_start;
  workOrder.appointment_confirmed_at = payload.appointment_confirmed_at;
  workOrder.appointment_duration_minutes = payload.appointment_duration_minutes;
  if (workOrder.appointment) {
    workOrder.appointment = {
      ...workOrder.appointment,
      start: payload.appointment_start,
      duration_minutes: payload.appointment_duration_minutes,
      confirmed_at: payload.appointment_confirmed_at,
    };
  } else {
    workOrder.appointment = {
      start: payload.appointment_start,
      duration_minutes: payload.appointment_duration_minutes,
      confirmed_at: payload.appointment_confirmed_at,
    };
  }
}

/**
 * Verkoper van de gekoppelde deal, zelfde bron als op de projectpagina.
 * @param {Object} workOrder
 * @returns {{ name: string, email: string|null }|null}
 */
export function salespersonFromWorkOrder(workOrder) {
  const owner = workOrder?.project?.deal?.owner;
  if (!owner) return null;
  const name = [owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.email || null;
  if (!name) return null;
  return { name, email: owner.email || null };
}

/**
 * Project waar deze werkbon onder hangt, voor de adminsnelkoppeling.
 * @param {Object} workOrder
 * @returns {{ id: string, label: string }|null}
 */
export function projectLinkFromWorkOrder(workOrder) {
  const project = workOrder?.project;
  const id = project?.id || workOrder?.project_id || null;
  if (!id) return null;
  const label = getWorkOrderTitle({ project }) || project?.project_number || project?.title || id;
  return { id, label };
}

/**
 * Display title for a work order: customer name + project number.
 * Falls back to project title or work order number when those are missing.
 * @param {Object} workOrder
 * @returns {string}
 */
export function getWorkOrderTitle(workOrder) {
  const project = workOrder?.project;
  const lead = project?.lead;
  const customer =
    lead?.name || [lead?.first_name, lead?.last_name].filter(Boolean).join(' ') || '';
  const parts = [customer, project?.project_number].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  return project?.title || workOrder?.work_order_number || '';
}

/**
 * Human message for a failed schedule/PATCH. Understands the server's
 * 409 `technician_conflict` (double booking) and planning `day_full`.
 * @param {*} error axios error
 * @param {string} [fallback]
 * @returns {string}
 */
export function scheduleErrorMessage(error, fallback = 'Kon werkbon niet inplannen') {
  const body = error?.response?.data || {};
  const fmt = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ''
      : d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  };
  if (body.code === 'technician_conflict') {
    const first = (body.conflicting_work_orders || [])[0];
    if (first) {
      return `Monteur heeft dan al ${first.work_order_number || 'een afspraak'} (${fmt(first.appointment_start)}–${fmt(first.appointment_end)})`;
    }
    return 'Monteur heeft op dat moment al een afspraak';
  }
  if (body.code === 'day_full') return 'Deze dag zit vol voor de monteur';
  if (body.code === 'work_order_not_schedulable') {
    return body.message || 'Werkbon is niet meer te plannen';
  }
  return fallback;
}
